import { Card, Col, Row, Statistic, Progress, Typography } from "antd";
import { ClockCircleOutlined } from "@ant-design/icons";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../core/data/redux/store";
import { useEffect, useMemo, useState } from "react";
import { fetchAttendanceData } from "../../../core/data/redux/hr-dashboard/hrDashboardAction";
import { getEmployeeByUserId } from "../../../core/data/redux/employee/employeeAction";
import Table from "../../../core/common/dataTable/index";

const { Text } = Typography;

const AttendanceOverview = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { attendanceData } = useSelector((state: RootState) => state.dashboard.hrDashboard);
  const { user } = useSelector((state: any) => state.auth);
  const { employeesbyUser } = useSelector((state: any) => state.employee);
  const employeeId = employeesbyUser?.employeeId;
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [selectedTableData, setSelectedTableData] = useState<any[]>([]);
  const [tableKey, setTableKey] = useState(0);
  const attendanceEmployees = useSelector((state: any) => state.dashboard.hrDashboard.attendanceEmployees);
  const [selectedDepartment, setSelectedDepartment] = useState("");


  const handleCardClick = (type: string) => {
    let data: any[] = [];

    switch (type) {
      case "overtime":
        data = attendanceEmployees?.overtime || [];
        break;

      case "leaves":
        data = attendanceEmployees?.leaves || [];
        break;

      case "lateCheckins":
        data = attendanceEmployees?.lateCheckins || [];
        break;

      default:
        data = [];
    }

    setSelectedCategory(type);
    setSelectedTableData(data);
    setTableKey(prev => prev + 1);
  };

  const getColumnsForCategory = (category: string) => {
    switch (category) {

      case "overtime":
        return [
          { title: "Employee ID", dataIndex: "EmployeeId" },
          { title: "Employee Name", dataIndex: "EmployeeName" },
          { title: "Department", dataIndex: "DepartmentName" },
          { title: "Designation", dataIndex: "DesignationName" },
          { title: "Overtime", dataIndex: "OvertimeHours" },
          {
            title: "Date", dataIndex: "OvertimeDate",
            render: (t: any) => t ? new Date(t).toLocaleDateString() : ""
          }
        ];

      case "leaves":
        return [
          { title: "Employee ID", dataIndex: "EmployeeId" },
          { title: "Employee Name", dataIndex: "EmployeeName" },
          { title: "Department", dataIndex: "DepartmentName" },
          { title: "Designation", dataIndex: "DesignationName" },
          { title: "Leave Type", dataIndex: "LeavePlanType" },
          {
            title: "From", dataIndex: "FromDate",
            render: (t: any) => new Date(t).toLocaleDateString()
          },
          {
            title: "To", dataIndex: "ToDate",
            render: (t: any) => new Date(t).toLocaleDateString()
          },
          { title: "Duration", dataIndex: "Duration" }
        ];

      case "lateCheckins":
        return [
          { title: "Employee ID", dataIndex: "EmployeeID" },
          { title: "Employee Name", dataIndex: "EmployeeName" },
          { title: "Department", dataIndex: "DepartmentName" },
          { title: "Designation", dataIndex: "DesignationName" },
          {
            title: "Punch Date", dataIndex: "PunchDate",
            render: (t: any) => new Date(t).toLocaleDateString()
          },
          { title: "Scheduled", dataIndex: "ScheduledStart" },
          { title: "Actual In", dataIndex: "ActualCheckIn" },
          { title: "Late (Minutes)", dataIndex: "LateByMinutes" }
        ];

      default:
        return [];
    }
  };

  const handleLateDeptClick = (entry: any) => {
    const dept = entry.department;
    setSelectedDepartment(dept);

    const filtered = attendanceEmployees?.lateCheckins?.filter((emp: any) => emp.DepartmentName === dept);

    setSelectedCategory("lateCheckins");
    setSelectedTableData(filtered);
    setTableKey(prev => prev + 1);
  };


  useEffect(() => {
    if (!employeesbyUser?.employeeId && user?.UserId) { dispatch(getEmployeeByUserId({ userId: user.UserId })); }
  }, [dispatch, employeesbyUser, user]);

  useEffect(() => {
    if (!employeeId) return;
    dispatch(fetchAttendanceData({ hrId: employeeId }));

    const interval = setInterval(() => {
      dispatch(fetchAttendanceData({ hrId: employeeId }));
    }, 60000);

    return () => clearInterval(interval);
  }, [dispatch, employeeId]);

  const attendance = attendanceData?.[0] || {};

  const [kpiData, setKpiData] = useState({
    absenteeismRate: 0,
    overtimeHours: "00:00:00",
    plannedLeave: 0,
    unplannedLeave: 0,
    plannedLeaveString: "0%",
    unplannedLeaveString: "0%",
    totalPercent: 0,
  });

  useEffect(() => {
    if (!attendance || Object.keys(attendance).length === 0) return;

    const absenteeismRateString = attendance.absenteeism_rate || "0%";
    const absenteeismRate = parseFloat(absenteeismRateString.replace("%", ""));

    const overtimeHours = attendance.overtime_hours || "00:00:00";

    const plannedLeaveString = attendance.planned_leave_utilization || "0%";
    const unplannedLeaveString = attendance.unplanned_leave_utilization || "0%";
    const plannedLeave = parseFloat(plannedLeaveString.replace("%", ""));
    const unplannedLeave = parseFloat(unplannedLeaveString.replace("%", ""));
    const totalPercent = plannedLeave + unplannedLeave;

    setKpiData({
      absenteeismRate,
      overtimeHours,
      plannedLeave,
      unplannedLeave,
      plannedLeaveString,
      unplannedLeaveString,
      totalPercent,
    });
  }, [attendance]);

  const lateCheckInsData = useMemo(() => {
    return (
      attendance.late_checkins_by_department?.map((item: any) => ({
        department: item.department,
        late: item.late_checkins,
      })) || []
    );
  }, [attendance.late_checkins_by_department]);

  return (
    <>
      <Row gutter={[24, 24]} className="mb-4">
        <Col xs={24} sm={12} lg={8}>
          <Card className="h-100">
            <Text className="d-block mb-2">Absenteeism Rate</Text>
            <Progress percent={kpiData.absenteeismRate} status="active" strokeColor="#ff4d4f" />
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={8}>
          <Card className="h-100" onClick={() => handleCardClick("overtime")} style={{ cursor: "pointer" }}>
            <Statistic
              title="Overtime Hours"
              value={kpiData.overtimeHours?.split(".")[0]}
              prefix={<ClockCircleOutlined className="text-warning" />}
              valueStyle={{ color: "#faad14" }}
            />
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={8}>
          <Card className="h-100" onClick={() => handleCardClick("leaves")} style={{ cursor: "pointer" }}>
            <Text className="d-block mb-2">Leave Utilization</Text>
            <Progress
              percent={kpiData.totalPercent}
              success={{ percent: kpiData.plannedLeave }}
              strokeColor="#1890ff"
              format={() => `${kpiData.plannedLeaveString} Planned / ${kpiData.unplannedLeaveString} Unplanned`}
            />
          </Card>
        </Col>
      </Row>
      {selectedCategory && (
        <div className="card mt-4">
          <div className="card-header">
            <h5 className="mb-0 text-capitalize">
              {selectedCategory === "lateCheckins"
                ? `Late Check-ins (${selectedDepartment})`
                : selectedCategory}
            </h5>
          </div>

          <div className="card-body p-0">
            <Table
              key={tableKey}
              dataSource={selectedTableData}
              columns={getColumnsForCategory(selectedCategory)}
            />
          </div>
        </div>
      )}


      <Row gutter={[24, 24]} className="mb-4">
        <Col xs={24}>
          <Card title="Late Check-ins by Department">
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={lateCheckInsData}>
                <XAxis dataKey="department" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="late" fill="#ff4d4f" onClick={(entry) => handleLateDeptClick(entry)} />
              </BarChart>
            </ResponsiveContainer>
          </Card>
        </Col>
      </Row>
    </>
  );
};

export default AttendanceOverview;
